import { pdf } from "@react-pdf/renderer";
import { useState } from "react";
import { useWindowSettings } from "../components/ProfileContext";
import { Inspection } from "../models/Inspection";
import { Api } from "./Api";
import { getDB } from "./db";

/**** Custom React Hooks and UI Helper Functions ****/

// This gives a state updater that also performs another (user-specified) update function
export function useChainedState(initial, updater) {
	let [val, setVal] = useState(initial);
	let newUpdater = (newVal) => {
		updater(newVal);
		setVal(newVal);
	}
	return [val, newUpdater];
}

// Returns a function which will force a page re-render
export function useForceRender() {
	let [val, setVal] = useState([]);
	let forceRender = () => {
		console.log("forcing re-render");
		setVal([]); // since every array is a new object, this forces a re-render
	};
	return forceRender;
}

// Returns "starting", "loading", "ready" - makes it easy to specify the criteria for a page to be booted.
// Expects a list of booleans where they are set to "true" if it is a completed start condition and "false" otherwise.
export function pageReadyCondition(...theStartConditions) {
	let allConditions = true;
	let anyCondition = theStartConditions.length == 0; // True by default if there are no conditions
	for(const condIdx in theStartConditions) {
		if(theStartConditions[condIdx]) {
			anyCondition = true;
		} else {
			allConditions = false;
		}
	}
	if(allConditions) {
		return "ready";
	}
	if(anyCondition) {
		return "loading";
	}
	return "starting";
}

// stub for now - eventually need more comprehensive information
export function getEnvironmentCameraSettings() {
	// Platform-specific settings
	if(getPlatform() == "ios") {
		return {
			needsMirroring: false,
		}
	}

	// Default settings
	return {
		needsMirroring: false,
	}
}

export function getPlatform() {
	if(window.iOSNative) {
		return "ios";
	}
	if(window.Android != null) {
		return "android";
	}

	return "browser";
}

export function getBrowser() {
	let userAgent = navigator.userAgent;
	
	if(userAgent.match(/chrome|chromium|crios/i)){
		return "chrome";
	}
	if(userAgent.match(/firefox|fxios/i)){
		return "firefox";
	}
	if(userAgent.match(/safari/i)){
		return "safari";
	}
	if(userAgent.match(/opr\//i)){
		return "opera";
	}
	if(userAgent.match(/edg/i)){
		return "edge";
	}

	return "unknown";
}

let advisoryLockout = false;
export function setNavAdvisoryLockout(value) {
	advisoryLockout = value;
}

export function getNavAdvisoryLockout() {
	return advisoryLockout;
}

export function navigateAdvisory(nav, destination) {
	if(advisoryLockout) {
		if(!confirm("Are you sure you want to navigate away from this page?  All changes will be lost.")) {
			return;
		}
	}
	nav(destination);
}

// Used for a permanent empty array to prevent state checking issues.
export const emptyArray = [];

/****** FORMATTING FUNCTIONS ********/

export function isStringBlank(str) {
	return str == "" || str == null;
}

export function stringToDate(str) {
	return new Date(Date.parse(str));
}

let monthShortNames = [ "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ];
export function getDateFormatter(dateFormat) {
	let separator = dateFormat.includes(".") ? "." : (dateFormat.includes("/") ? "/" : "-");
	let origComponents = dateFormat.split(separator);

	let yearPosition = origComponents.indexOf("yyyy");
	let monthPosition = origComponents.indexOf("MM");
	let longMonthPosition = origComponents.indexOf("MMM");
	let dayPosition = origComponents.indexOf("dd");	

	return function(dt) {
		if(dt == null) {
			return null;
		}
		if(isNaN(dt.getTime())) { // Usually from a failed date parse
			return null;
		}
		let dateInfo = [];
		dateInfo[yearPosition] = dt.getFullYear();
		if(longMonthPosition == -1) {
			let m = dt.getMonth() + 1; // zero-based
			if(m < 10) {
				m = "0" + m;
			}
			dateInfo[monthPosition] = m;
		} else {
			dateInfo[longMonthPosition] = monthShortNames[dt.getMonth()];
		}
		let d = dt.getDate();
		if(d < 10) {
			d = "0" + d;
		}
		dateInfo[dayPosition] = d;
		return dateInfo.join(separator);
	};
}

export function isoTimestamp() {
	return (new Date()).toISOString();
}

export function formatFilename(ext, ...components) {
	let fname = components.join("_");
	fname = fname.replaceAll(/[^0-9a-zA-Z]/g, "_"); // Get rid of non-alphanumeric characters
	fname = fname.replaceAll(/_+/g, "_"); // get rid of consecutive _'s
	return fname + "." + ext;
}

/**** INSPECTIONS NETWORK FUNCTIONS *****/

export async function inspectionSearch(inspId) {
	return await Api.forProfile(null).inspectionCall(`/v2/inspections/external/${encodeURIComponent(inspId)}`);
}

export async function archiveComponent(user, assetNumber) {
	return await Api.forProfile(user).inspectionCall(`/v2/components/${encodeURIComponent(assetNumber)}/archive`, "PUT")
}

/**** General Helper Functions ****/

export function listMemberForId(list, memberId) {
	if(list == null) { return null; }
	return list.filter(x => x.id == memberId)[0];
}

export function positionSort(a, b) {
	return (a.position || 0) - (b.position || 0);
}

export function arrayElementMove(ary, elem, offset) {
	let idx = ary.indexOf(elem);

	// Check for boundary conditions
	if(idx == 0 && offset == -1){
		return ary;
	}
	if(idx == ary.length - 1 && offset == 1){
		return ary;
	}
	
	return ary.map(
		(x, i) => {
			if (i == idx){
				return ary[i+offset];
			} else if (i == idx + offset) {
				return ary[i - offset];
			} else {
				return x;
			}
		}
	)
}

export function getRandomInt(size = 100000) {
	return Math.floor(Math.random() * size)
}

// previd is a "previous id" that overrides getting a random ID
export function getRandomId(previd) {
	if(previd == null || previd == "") {
		return "control_" + getRandomInt();
	}
	return previd;
}

export function groupListBy(array, keyPath) {
	let result = {};
	array.forEach(x => {
		let keyval = valueForKeyPath(x, keyPath);
		if(keyval == null || keyval == "") {
			keyval = "* None *"
		}
		let lst = result[keyval] || [];
		lst.push(x);
		result[keyval] = lst;
	});
	return result;
}

export function valueForKeyPath(obj, keyPath) {
	let keyval = obj;
	if(Array.isArray(keyPath)) {
		keyPath.forEach(kp => {
			keyval = keyval?.[kp];
		});
	} else {
		if(keyPath instanceof Function) {
			keyval = keyPath(obj);
		} else {
			if(keyPath != null) {
				keyval = obj?.[keyPath];
			}	
		}
	}
	return keyval;
}

export function sortByKeyPath(keyPath, ascending = true, ignoreCase = true) {
	return (a, b) => {
		let valA = valueForKeyPath(a, keyPath) || "";
		if(ignoreCase) {
			valA = valA.toLowerCase();
		}
		let valB = valueForKeyPath(b, keyPath) || "";
		if(ignoreCase) {
			valB = valB.toLowerCase();
		}
		if(valA > valB) {
			return ascending ? 1 : -1;
		}
		if(valA < valB) {
			return ascending ? -1 : 1;
		}
		return 0;
	};
}

export async function openLinkExternal(url) {
	let link = document.createElement("a");
	link.style.display = "none";
	link.setAttribute("href", url);
	link.setAttribute("target", "_blank");
	// popup.document.body.appendChild(link);
	document.body.appendChild(link);
	link.click();

	// Make sure this stays long enough to click, but then goes away.  Not sure if necessary.
	setTimeout(() => {document.body.removeChild(link);}, 5000);
}

export async function downloadPdfDocumentToNewWindow(pdfDoc, fname) {
	if(fname == null) {
		fname = "document.pdf";
	}
	let pdfData = pdf(pdfDoc);
	let blob = await pdfData.toBlob();	
	downloadBlobToNewWindow(blob, fname);
}

export async function downloadBlobToNewWindow(blob, filename) {
	let blobUrl = window.URL.createObjectURL(blob);	
	console.log("Blob URL: " + blobUrl);

	// See https://developer.apple.com/forums/thread/119017
	// Popup is not working for me - doing it in main window.
	// let popup = window.open();
	let link = document.createElement("a");
	link.style.display = "none";
	link.setAttribute("href", blobUrl);
	link.setAttribute("download", filename);
	// popup.document.body.appendChild(link);
	document.body.appendChild(link);
	link.click();

	// Make sure this stays long enough to click, but then goes away.  Not sure if necessary.
	setTimeout(() => {document.body.removeChild(link);}, 5000);
}

export function parsePossibleMcElroyQrCode(code) {
	if(!code.startsWith("https://www.mcelroy.com/lookup/?")) {
		return null;
	}
	let paramStr = code.split("?")[1];
	let paramAry = paramStr.split("&");
	let result = {};
	paramAry.forEach(entry => {
		let [k, v] = entry.split("=");
		result[k] = decodeURIComponent(v);
	});
	return result;
}

export async function navigateToInspectionId(profile, navigate, inspId, sharingCode) {
	let db = await getDB();
	let tmpInsp = null;

	// Is it local?  Just go there
	try {
		tmpInsp = await db.get("Inspections", inspId);
	} catch(err) {
		console.log("not in db");
		// Not in DB
	}
	if(tmpInsp == null) {
		// Is it remote?  Load it first
		tmpInsp = await Inspection.loadInspection(profile, inspId, sharingCode);
	}

	// Go to the inspection
	navigate(`/inspections/${encodeURIComponent(tmpInsp.id)}`);
}

export function arrayToCSV(ary) {
	let data = "";
	ary.forEach(row => {
		let rowString = row.map(col => `"${(col == null ? "" : col.toString()).replaceAll('"', '').replaceAll("\n", "").replaceAll("\r", "")}"`).join(",");
		data = data + rowString + "\r\n";
	});
	return data;
}

export function useIsVault() {
	let [windowSettings] = useWindowSettings();
	return windowSettings?.context == "VAULT";
}

export function filterByKeyPaths(data, filterString, keyPaths) {
	if(!filterString) {
		return data;
	}
	if(data == null) {
		return data;
	}

	let lcFilterString = filterString.toLowerCase();
	let filteredData = data.filter(info => {
		let found = false;
		keyPaths.forEach(kp => {
			let val = valueForKeyPath(info, kp);
			if(val != null) {
				val = val.toString().toLowerCase();
				if(val.includes(lcFilterString)) {
					found = true;
				}
			}
		});
		return found;
	});
	return filteredData;
}

export function blobToDataUrl(blob) {
	return new Promise((resolve, reject) => {
		let reader = new FileReader();
		reader.onloadend = () => {
			resolve(reader.result);
		}
		reader.onerror = () => {
			reject(reader.error);
		}
		reader.readAsDataURL(blob);
	});
}

export async function blobUrlToDataUrl(blobUrl) {
	let response = await fetch(blobUrl);
	let blob = await response.blob();
	let dataUrl = await blobToDataUrl(blob);
	return dataUrl;
}

export function delay(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

// This function is just a renaming of setTimeout, but indicates
// that the point of using it is to give us a delay to render
// any changes before executing this code.
// Also, we can modify the delay if we ever need to.
// Should use this for SpinnerButton interactions.
export function afterRenderDelay(f) {
	setTimeout(f, 100);
}

export async function uncacheUrl(url) {
	return await fetch(url, {
		headers: {
			"Pragma": "no-cache",
			"Expires": -1,
			"Cache-Control": "no-cache"
		}
	});
}
